import { useQuery } from '@tanstack/react-query'
import { healthApi, ServiceStatus } from '../api/health'
import { AppLayout } from '../components/AppLayout'
import { Activity, RefreshCw, CheckCircle, AlertTriangle, Server, Clock, Database, Bot, Users, FileEdit, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function HealthPage() {
  const { data: health, isLoading, refetch, error } = useQuery({
    queryKey: ['health'],
    queryFn: () => healthApi.getHealth(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  })

  return (
    <AppLayout
      breadcrumbs={[{ label: 'Stato del sistema' }]}
      logoIcon={<Activity className="size-4 text-primary" />}
      topbarActions={
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="size-3.5" />
          Aggiorna
        </Button>
      }
      mainClassName="mail-utility-page"
    >
<header className="mb-6"><h2 className="text-xl font-semibold">Stato del sistema</h2><p className="text-sm text-muted-foreground mt-1">Disponibilità dei servizi e carico di lavoro dell’ospedale</p></header>
      {isLoading ? (
        <div className="text-center text-muted-foreground py-8">Caricamento...</div>
      ) : error ? (
        <div className="max-w-4xl mx-auto space-y-6">
          <Card className="border-danger/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="size-8 text-danger" />
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Errore di connessione</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Impossibile contattare il server. Verifica che il backend sia in esecuzione.
                  </p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                    <RefreshCw className="size-3.5" />
                    Riprova
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : health ? (
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Overall Status */}
          <Card className={
            health.status === 'ok'
              ? 'border-success/20'
              : 'border-warning/20'
          }>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                {health.status === 'ok' ? (
                  <CheckCircle className="size-8 text-success" />
                ) : (
                  <AlertTriangle className="size-8 text-warning" />
                )}
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {health.status === 'ok' ? 'Tutti i servizi operativi' : 'Servizi degradati'}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Versione: {health.version} — Ultimo check: {new Date(health.timestamp).toLocaleTimeString('it-IT')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services */}
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 bg-muted/30 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                  <Server className="size-4 text-muted-foreground" />
                  Servizi
                </h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Servizio</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Latenza</TableHead>
                    <TableHead>Errore</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(health.services).map(([name, svc]: [string, ServiceStatus]) => (
                    <TableRow key={name}>
                      <TableCell className="font-medium capitalize">{name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          svc.status
                            ? 'bg-success/10 text-success border-success/20'
                            : 'bg-danger/10 text-danger border-danger/20'
                        }>
                          {svc.status ? 'OK' : 'ERRORE'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-muted-foreground">
                        {svc.latency_ms !== null ? `${svc.latency_ms} ms` : '-'}
                      </TableCell>
                      <TableCell className="text-danger truncate max-w-xs">
                        {svc.error || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Database Stats */}
          {health.queue_stats && (
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-3 bg-muted/30 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <Database className="size-4 text-muted-foreground" />
                    Database Overview
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <TrendingUp className="size-3" />
                          Totale Casi
                        </div>
                        <div className="text-xl font-bold text-foreground">{health.queue_stats.total_cases}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <TrendingUp className="size-3" />
                          Totale Email
                        </div>
                        <div className="text-xl font-bold text-foreground">{health.queue_stats.total_emails}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Users className="size-3" />
                          Casi assegnati
                        </div>
                        <div className="text-xl font-bold text-success">{health.queue_stats.cases_with_vet}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Bot className="size-3" />
                          Email con summary AI
                        </div>
                        <div className="text-xl font-bold text-primary">{health.queue_stats.emails_with_summary}</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* AI Coverage */}
                  {health.queue_stats.total_emails > 0 && (
                    <div className="mb-6">
                      <div className="text-sm text-muted-foreground mb-2">Copertura AI Summary</div>
                      <Progress
                        value={(health.queue_stats.emails_with_summary / health.queue_stats.total_emails) * 100}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {((health.queue_stats.emails_with_summary / health.queue_stats.total_emails) * 100).toFixed(1)}% delle email elaborate da AI
                      </p>
                    </div>
                  )}

                  {/* Queue Stats */}
                  <div className="border-t border-border pt-4">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Clock className="size-3.5" />
                      Coda
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-xs text-muted-foreground">Casi non assegnati</div>
                          <div className="text-lg font-bold text-warning">{health.queue_stats.unassigned_cases}</div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-xs text-muted-foreground">Casi in attesa</div>
                          <div className="text-lg font-bold text-info">{health.queue_stats.pending_cases}</div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-xs text-muted-foreground">Email nuove</div>
                          <div className="text-lg font-bold text-success">{health.queue_stats.new_emails}</div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <FileEdit className="size-3" />
                            Draft in attesa
                          </div>
                          <div className="text-lg font-bold text-amber-600">{health.queue_stats.draft_emails}</div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {health.queue_stats.last_processed_email && (
                    <p className="text-xs text-muted-foreground mt-4">
                      Ultima email processata: {new Date(health.queue_stats.last_processed_email).toLocaleString('it-IT')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <div className="text-center text-muted-foreground py-8">Nessun dato disponibile</div>
      )}
    </AppLayout>
  )
}
